import {
  findBoundaries,
  flowNeighbors,
  isLocalPipe,
  isLocalPipingNode,
  pipeDirection,
} from './flowGraph'
import type { FlowState, FlowStep } from './flowTypes'
import type { PnidGraph } from './graphIndex'
import type { GraphNode } from './graphTypes'

function reachableViaIds(
  seeds: Iterable<GraphNode>,
  pageId: string,
  canCross: (step: FlowStep) => boolean
): Set<string> {
  const reached = new Set(seeds)
  const queue = [...reached]
  const crossed = new Set<string>()
  for (const current of queue)
    for (const step of flowNeighbors(current, pageId)) {
      if (!canCross(step)) continue
      crossed.add(step.via.id)
      if (reached.has(step.to)) continue
      reached.add(step.to)
      queue.push(step.to)
    }
  return crossed
}

export function computePipeStates(
  graph: PnidGraph,
  stoppedNodeIds: Iterable<string>
): Map<string, FlowState> {
  const { pageId } = graph
  const nodes = graph.getNodes()
  const pipes = nodes.filter((node) => isLocalPipe(node, pageId))
  const pipingNodes = nodes.filter((node) => isLocalPipingNode(node, pageId))
  const vias = new Set(
    pipingNodes.flatMap((node) =>
      flowNeighbors(node, pageId).map(({ via }) => via.id)
    )
  )
  const stops = new Set([...stoppedNodeIds].filter((id) => vias.has(id)))
  const stateWithoutStops = (pipe: GraphNode): FlowState =>
    vias.has(pipe.id) && pipeDirection(pipe) !== 'unknown'
      ? 'enabled'
      : 'unknown'
  if (!stops.size)
    return new Map(pipes.map((pipe) => [pipe.id, stateWithoutStops(pipe)]))

  const anyStep = (): boolean => true
  const isOpen = (step: FlowStep): boolean => !stops.has(step.via.id)
  const reach = (
    seeds: Iterable<GraphNode>,
    canCross: (step: FlowStep) => boolean
  ): Set<string> => reachableViaIds(seeds, pageId, canCross)
  const boundaries = findBoundaries(graph)
  const reachableBeforeStops = reach(boundaries, anyStep)
  const definitelyReachable = reach(
    boundaries,
    (step) => step.certain && isOpen(step)
  )
  const possiblyReachable = reach(boundaries, isOpen)
  const connectedToStops = reach(
    pipingNodes.filter((node) =>
      flowNeighbors(node, pageId).some(({ via }) => stops.has(via.id))
    ),
    anyStep
  )
  const state = (pipe: GraphNode): FlowState => {
    if (stops.has(pipe.id)) return 'blocked'
    if (vias.has(pipe.id) && reachableBeforeStops.has(pipe.id)) {
      if (definitelyReachable.has(pipe.id)) return 'enabled'
      return !possiblyReachable.has(pipe.id) && !graph.isIncomplete
        ? 'blocked'
        : 'unknown'
    }
    return connectedToStops.has(pipe.id) ? 'unknown' : stateWithoutStops(pipe)
  }
  return new Map(pipes.map((pipe) => [pipe.id, state(pipe)]))
}

export function isolationStates(
  states: Map<string, FlowState>
): Map<string, FlowState> {
  return new Map(
    [...states].map(([id, state]) => [
      id,
      state === 'blocked' ? 'blocked' : 'enabled',
    ])
  )
}
